import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Forward, Loader2, Reply, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { errorMessage, submissionsApi } from "../../lib/api";
import { STATUS_OPTIONS, cleanEmails, formLabel, splitEmails } from "../../lib/forms";
import { cn } from "../../lib/utils";
import { EmailStatus, FormTypeLabel, StatusBadge, fmtDateTime } from "./SubmissionsTable";

const FIELD_LABELS = { first_name: "First name", last_name: "Last name", name: "Name", email: "Email", phone: "Phone", message: "Message", agreed: "Agreed to Avalon's ethos" };
const inputCls = "h-10 rounded-none border-border bg-transparent px-3 text-sm focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0";
const Eyebrow = ({ children }) => <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">{children}</p>;

const Fields = ({ fields }) => (
  <dl className="divide-y divide-border border border-border" data-testid="submission-fields">
    {Object.entries(fields || {}).map(([k, v]) => (
      <div key={k} className="grid gap-1 px-4 py-3 sm:grid-cols-[130px_1fr]">
        <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{FIELD_LABELS[k] || k}</dt>
        <dd className="whitespace-pre-line text-sm leading-relaxed">{typeof v === "boolean" ? (v ? "Yes" : "No") : String(v || "—")}</dd>
      </div>
    ))}
  </dl>
);

const Source = ({ s }) => {
  const utm = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].filter((k) => s[k]);
  return (
    <div className="space-y-1 text-sm" data-testid="submission-source">
      {s.page_title ? (
        <p>
          Landing page: <Link to={`/admin/pages/${s.page_id}`} className="font-semibold text-primary hover:underline">{s.page_title}</Link>{" "}
          <a href={`/lp/${s.page_slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            /lp/{s.page_slug} <ExternalLink className="h-3 w-3" />
          </a>
        </p>
      ) : (
        <p>Page: <span className="font-semibold">{s.path || "—"}</span></p>
      )}
      {s.referrer && <p className="text-xs text-muted-foreground">Referrer: {s.referrer}</p>}
      {utm.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Campaign: {utm.map((k) => `${k.slice(4)}=${s[k]}`).join(" · ")}
        </p>
      )}
    </div>
  );
};

const EmailLog = ({ emails }) => (
  <ul className="divide-y divide-border border border-border text-sm" data-testid="submission-email-log">
    {!emails?.length && <li className="px-4 py-3 text-muted-foreground">No emails sent yet.</li>}
    {(emails || []).map((e, i) => (
      <li key={`${e.kind}-${i}`} className="flex flex-wrap items-start justify-between gap-2 px-4 py-3">
        <div className="min-w-0">
          <p className="font-semibold capitalize">{e.kind === "notify" ? "Team notification" : e.kind === "autoreply" ? "Automatic follow-up" : e.kind}</p>
          <p className="truncate text-xs text-muted-foreground">{e.to?.length ? `To ${e.to.join(", ")}` : "No recipients"} · {fmtDateTime(e.sent_at)}</p>
          {e.error && <p className="mt-1 text-xs text-amber-400">{e.error}</p>}
        </div>
        <span className={cn("text-[10px] font-semibold uppercase tracking-[0.2em]", e.status === "sent" ? "text-emerald-400" : e.status === "failed" ? "text-destructive" : "text-muted-foreground")}>{e.status}</span>
      </li>
    ))}
  </ul>
);

const ForwardForm = ({ id, onSent }) => {
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const list = cleanEmails(splitEmails(to));
    if (!list.length) return toast.error("Enter at least one email address");
    setBusy(true);
    try {
      const res = await submissionsApi.forward(id, list, note);
      toast.success("Forwarded", { description: `Sent to ${list.join(", ")}` });
      setTo("");
      setNote("");
      onSent(res.log);
    } catch (err) {
      toast.error(errorMessage(err, "Could not forward"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 border border-border bg-background/40 p-4" data-testid="forward-form">
      <div className="space-y-1.5">
        <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Forward to (comma separated)</Label>
        <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="artist@studio.com, manager@studio.com" className={inputCls} data-testid="forward-to" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Note (optional)</Label>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Can you take this one?" className="min-h-[70px] rounded-none border-border bg-transparent text-sm" data-testid="forward-note" />
      </div>
      <Button onClick={send} disabled={busy} size="sm" className="h-10 rounded-none px-5 text-[11px] font-semibold uppercase tracking-[0.2em]" data-testid="forward-send">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Forward className="mr-2 h-4 w-4" />} Forward formatted message
      </Button>
    </div>
  );
};

/** Loads one submission and exposes patch/delete helpers that keep the list in sync. */
const useSubmission = (id, { onClose, onChanged, onDeleted }) => {
  const [s, setS] = useState(null);

  useEffect(() => {
    if (!id) return;
    setS(null);
    submissionsApi
      .get(id)
      .then((doc) => {
        setS(doc);
        onChanged(doc);
      })
      .catch((err) => {
        toast.error(errorMessage(err, "Could not load submission"));
        onClose();
      });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps -- reload only when a different submission is opened

  const patch = async (body, okMsg) => {
    try {
      const doc = await submissionsApi.update(id, body);
      setS(doc);
      onChanged(doc);
      if (okMsg) toast.success(okMsg);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const remove = async () => {
    try {
      await submissionsApi.remove(id);
      toast.success("Submission deleted");
      onDeleted(id);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const appendLog = (log) => setS((prev) => ({ ...prev, emails: [...(prev.emails || []), log] }));
  return { s, patch, remove, appendLog };
};

const DetailHeader = ({ s, patch, remove }) => (
  <SheetHeader className="space-y-3 border-b border-border px-6 py-5 text-left">
    <div className="flex items-center gap-3">
      <FormTypeLabel type={s.form_type} />
      <StatusBadge status={s.status} />
    </div>
    <SheetTitle className="display text-4xl uppercase leading-none tracking-wide">{s.name || formLabel(s.form_type)}</SheetTitle>
    <SheetDescription className="text-sm">
      <a href={`mailto:${s.email}`} className="text-primary hover:underline" data-testid="submission-email">{s.email}</a>
      {s.fields?.phone && <span> · {s.fields.phone}</span>} · {fmtDateTime(s.created_at)}
    </SheetDescription>
    <div className="flex flex-wrap gap-2">
      <Button asChild size="sm" className="h-9 rounded-none text-[10px] font-semibold uppercase tracking-[0.18em]" data-testid="submission-reply">
        <a href={`mailto:${s.email}?subject=${encodeURIComponent(`Re: your ${formLabel(s.form_type).toLowerCase()} — Avalon Tattoo Collective`)}`} onClick={() => s.status !== "replied" && patch({ status: "replied" })}>
          <Reply className="mr-2 h-3.5 w-3.5" /> Reply by email
        </a>
      </Button>
      <Button size="sm" variant="ghost" className="h-9 rounded-none text-[10px] font-semibold uppercase tracking-[0.18em] hover:text-destructive" onClick={remove} data-testid="submission-delete">
        <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete
      </Button>
    </div>
  </SheetHeader>
);

const StatusButtons = ({ status, onSelect }) => (
  <div className="flex flex-wrap gap-2" data-testid="submission-status-buttons">
    {STATUS_OPTIONS.map((o) => (
      <button
        key={o.id}
        type="button"
        onClick={() => onSelect(o.id)}
        className={cn(
          "border px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] transition-colors",
          status === o.id ? "border-primary bg-primary text-primary-foreground" : "border-border text-foreground/80 hover:border-foreground/50"
        )}
        data-testid={`set-status-${o.id}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const NotesEditor = ({ s, patch }) => {
  const [notes, setNotes] = useState(s.notes || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    await patch({ notes }, "Notes saved");
    setSaving(false);
  };
  return (
    <>
      <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Only visible to the team." className="min-h-[90px] rounded-none border-border bg-transparent text-sm" data-testid="submission-notes" />
      <Button size="sm" variant="outline" className="h-9 rounded-none text-[10px] font-semibold uppercase tracking-[0.18em]" onClick={save} disabled={saving || notes === (s.notes || "")} data-testid="submission-save-notes">
        Save notes
      </Button>
    </>
  );
};

/** Slide-over with the full submission, status controls, notes, email log and forwarding. */
const SubmissionDetail = ({ id, onClose, onChanged, onDeleted }) => {
  const { s, patch, remove, appendLog } = useSubmission(id, { onClose, onChanged, onDeleted });

  return (
    <Sheet open={Boolean(id)} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto border-border bg-background p-0 sm:max-w-xl" data-testid="submission-detail">
        {s && (
          <div className="flex min-h-full flex-col">
            <DetailHeader s={s} patch={patch} remove={remove} />

            <div className="flex-1 space-y-8 px-6 py-6">
              <section className="space-y-3">
                <Eyebrow>Status</Eyebrow>
                <StatusButtons status={s.status} onSelect={(status) => patch({ status })} />
              </section>

              <section className="space-y-3">
                <Eyebrow>Submitted details</Eyebrow>
                <Fields fields={s.fields} />
              </section>

              <section className="space-y-3">
                <Eyebrow>Where it came from</Eyebrow>
                <Source s={s} />
              </section>

              <section className="space-y-3">
                <Eyebrow>Internal notes</Eyebrow>
                <NotesEditor key={s.id} s={s} patch={patch} />
              </section>

              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <Eyebrow>Email delivery</Eyebrow>
                  <EmailStatus emails={s.emails} />
                </div>
                <EmailLog emails={s.emails} />
              </section>

              <section className="space-y-3">
                <Eyebrow>Forward to someone</Eyebrow>
                <ForwardForm id={s.id} onSent={appendLog} />
              </section>
            </div>
          </div>
        )}
        {!s && (
          <div className="flex h-full items-center justify-center">
            <SheetTitle className="sr-only">Loading submission</SheetTitle>
            <SheetDescription className="sr-only">Please wait</SheetDescription>
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default SubmissionDetail;
